import { useState, useCallback, useRef } from 'react';
import { useIntl } from 'react-intl';

import { useAppDispatch, useAppSelector, useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import { markConceptGroupSelectionsAsReported } from '@verantos-dev/app-state-cohort-builder';
import {
  awaitNotSaving,
  useCohortDefinitionStore,
} from '@verantos-dev/app-state-cohort-definition';
import { emitSyncConceptGroupSelections } from '@verantos-dev/app-state-research-assistant';
import {
  addResearchAssistantMessages,
  ModelVersion,
  selectResearchAssistantIsWaitingForResponse,
  selectResearchAssistantModel,
  setProgressStep,
  setResearchAssistantModel,
  useSetResearchAssistantToProcessing,
} from '@verantos-dev/app-state-research-assistant';
import { useAuth } from '@verantos-dev/core-auth';
import { useSafeRouteParams } from '@verantos-dev/data-access-cohorts';
import { ProgressStepType, ResearchAssistantMessageEnumLegacy } from '@verantos-dev/types-common';

import { useFileUploadDragDropTexts } from './useFileUploadDragDropTexts';
import { useResearchAssistantFileUploadState } from './useResearchAssistantFileUploadState';
import { emitUserPromptLegacy } from '../../namespaces/emitters/chatWindowEvents';

export const useResearchAssistantPromptState = () => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const { projectId, cohortId, cohortVersionId } = useSafeRouteParams();
  const { user } = useAuth();
  const isSyncingRef = useRef(false);

  const [userPrompt, setUserPrompt] = useState<string>('');
  const isWaitingForResponse = useAppSelector(selectResearchAssistantIsWaitingForResponse);
  const model = useAppSelector(selectResearchAssistantModel);
  const setResearchAssistantToProcessingWithAutoTimeout = useSetResearchAssistantToProcessing();
  const { fileUploadConfig, encodeFile } = useResearchAssistantFileUploadState();
  // TODO: cleanup with explicit_save ff
  const isExplicitSave = useFeatureFlagSplit('explicit_save');
  const fileUploadDragDropTexts = useFileUploadDragDropTexts({
    maxFileSizeMB: fileUploadConfig?.maxFileSizeMB,
    accept: fileUploadConfig?.accept,
  });

  const onUserPromptChange = (promptValue: string) => {
    setUserPrompt(promptValue);
  };

  const onModelChange = (modelVersion: ModelVersion) => {
    dispatch(setResearchAssistantModel(modelVersion));
    dispatch(
      addResearchAssistantMessages([
        {
          message: intl.formatMessage(
            {
              id: 'projects.cohortBuilderPage.researchAssistant.updateModelVersionInfoMessage',
            },
            {
              oldModelVersion: model,
              newModelVersion: modelVersion,
            }
          ),
          timestamp: new Date().toISOString(),
          currentUser: 'user',
          isCurrentUser: true,
          type: ResearchAssistantMessageEnumLegacy.MODEL_CHANGE,
        },
      ])
    );
  };

  const sendUserPrompt = useCallback(async () => {
    if (isWaitingForResponse) {
      return;
    }
    if (userPrompt.trim() === '' && !fileUploadConfig?.selectedFile) {
      return;
    }
    setResearchAssistantToProcessingWithAutoTimeout();

    if (cohortId && !isSyncingRef.current) {
      isSyncingRef.current = true;

      try {
        dispatch(markConceptGroupSelectionsAsReported({ markAll: true }));
        useCohortDefinitionStore
          .getState()
          .markConceptGroupSelectionsAsReported({ markAll: true, statuses: ['warning'] });

        emitSyncConceptGroupSelections({ cohortId, markAll: true });

        // Small delay to reduce race condition with backend
        await new Promise((resolve) => setTimeout(resolve, 50));
      } catch (error) {
        console.error('Failed to emit sync concept group selections:', error);
      } finally {
        isSyncingRef.current = false;
      }
    }

    dispatch(
      addResearchAssistantMessages([
        {
          message: fileUploadConfig?.selectedFileName
            ? intl.formatMessage(
                {
                  id: 'projects.researchAssistant.chatWindow.fileUpload.uploadedMessage',
                },
                {
                  userPrompt,
                  fileName: fileUploadConfig.selectedFileName,
                  newline: userPrompt && userPrompt.trim() !== '' ? '\n' : '',
                }
              )
            : userPrompt,
          timestamp: new Date().toLocaleTimeString(),
          currentUser: '',
          isCurrentUser: true,
        },
      ])
    );

    dispatch(setProgressStep({ step: ProgressStepType.UNDERSTANDING_REQUEST }));

    const fileData = fileUploadConfig?.selectedFile
      ? await encodeFile(fileUploadConfig.selectedFile)
      : undefined;

    // TODO: cleanup with explicit_save ff — await any in-flight save before RA send
    if (isExplicitSave) {
      await awaitNotSaving();
    }

    emitUserPromptLegacy({
      userPrompt,
      fileData: fileData as unknown as Record<string, unknown> | undefined,
      model,
      cohortId: cohortId || '',
      projectId: projectId || '',
      cohortVersionId: cohortVersionId || '',
      user,
    });
    setUserPrompt('');
    fileUploadConfig?.onClearSelectedFile?.();
  }, [
    cohortId,
    cohortVersionId,
    dispatch,
    isExplicitSave,
    isWaitingForResponse,
    model,
    projectId,
    setResearchAssistantToProcessingWithAutoTimeout,
    user,
    userPrompt,
    fileUploadConfig,
    encodeFile,
  ]);

  return {
    userPrompt,
    onUserPromptChange,
    onModelChange,
    sendUserPrompt,
    fileUploadConfig,
    fileUploadDragDropTexts,
  };
};
